// Types
import type { Action } from "./types";

/**
 * A super admin puts money into a postpay member's account, either one member
 * at a time or from an uploaded file.
 *
 * Every row is submitted on its own so that one bad row cannot take a whole
 * file down with it, and each row carries an idempotency key so that a
 * re-uploaded file or a double-clicked button does not load twice. The store
 * therefore holds a list of per-row results rather than a single outcome.
 */

/**
 * Submits the rows the operator has confirmed. `data.businessGuid` names the
 * company every row belongs to, `data.rows` are the loads themselves.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const submitFundsLoad = (data: any): Action => ({
  type: "SUBMIT_FUNDS_LOAD",
  data,
});

/**
 * Records the outcome of one row as soon as it is known, so a long file
 * reports progress while it runs instead of staying blank until the end.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const setFundsLoadRowResult = (data: any): Action => ({
  type: "SET_FUNDS_LOAD_ROW_RESULT",
  data,
});

/**
 * Marks the whole submission as finished, successfully or not.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const setFundsLoadCompleted = (data: any): Action => ({
  type: "SET_FUNDS_LOAD_COMPLETED",
  data,
});

/**
 * @return {*} {Action}
 */
export const clearFundsLoad = (): Action => ({
  type: "CLEAR_FUNDS_LOAD",
});

/**
 * Loads the postpay companies that can be loaded.
 *
 * This is a purpose-built read rather than the super admin company list,
 * because that list does not return the payment type at all, so prepay
 * companies cannot be told apart from postpay ones in the browser.
 *
 * @return {*} {Action}
 */
export const fetchLoadableBusinesses = (): Action => ({
  type: "FETCH_LOADABLE_BUSINESSES",
});

/**
 * @param {*} data
 * @return {*} {Action}
 */
export const setLoadableBusinesses = (data: any): Action => ({
  type: "SET_LOADABLE_BUSINESSES",
  data,
});

/**
 * Loads the members of one company once it has been picked. One company is a
 * small enough list to filter in the dropdown itself, so no search round trip
 * is needed per keystroke.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const fetchFundsLoadMembers = (data: any): Action => ({
  type: "FETCH_FUNDS_LOAD_MEMBERS",
  data,
});

/**
 * @param {*} data
 * @return {*} {Action}
 */
export const setFundsLoadMembers = (data: any): Action => ({
  type: "SET_FUNDS_LOAD_MEMBERS",
  data,
});
